import {useCallback, useEffect, useState} from 'react';
import {MainViewContainer} from '../../common/MainViewContainer/MainViewContainer';
import {listShiftSettings, updateShiftSetting} from '@digistaff/business';
import {app} from '../../providers/Account';
import {
  FormControlLabel,
  Checkbox,
  Box,
  CircularProgress,
  Tooltip,
  Button,
  Stack,
  FormHelperText,
} from '@mui/material';
import {Toast} from '../../common/Alerts/Toast';
import {ToastStruct} from '../../types/types';

export const ShiftSettingsView = () => {
  const [loading, setLoading] = useState(true);
  const [complianceOverride, setComplianceOverride] = useState(true);
  // null = this agency's DB doesn't have the sms_reply_auto_apply column
  const [smsAutoApply, setSmsAutoApply] = useState<boolean | null>(null);
  const [settingsId, setSettingsId] = useState('');
  const [saving, setSaving] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastData, setToastData] = useState<ToastStruct>({
    severity: 'info',
    text: '',
    verticalPos: 'bottom',
    horizontalPos: 'center',
  });

  const getSettings = useCallback(async () => {
    const result = await listShiftSettings(app);
    setSettingsId(result.data.listShiftSettings[0].id);
    setComplianceOverride(
      result.data.listShiftSettings[0].is_compliance_override
    );
    setSmsAutoApply(
      result.data.listShiftSettings[0].sms_reply_auto_apply ?? null
    );
    setLoading(false);
  }, []);

  useEffect(() => {
    getSettings();
  }, [getSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await updateShiftSetting(app, settingsId, {
        is_compliance_override: complianceOverride,
        ...(smsAutoApply !== null && {sms_reply_auto_apply: smsAutoApply}),
      });

      if (result.data.updateShiftSetting.id) {
        setToastData({
          ...toastData,
          text: 'Shift settings have been saved',
          severity: 'success',
        });
      } else {
        setToastData({
          ...toastData,
          text: 'Could not update shift settings',
          severity: 'error',
        });
      }
    } catch (error) {
      setToastData({
        ...toastData,
        text: 'Could not update shift settings',
        severity: 'error',
      });
    } finally {
      setSaving(false);
      setShowToast(true);
    }
  };

  return (
    <MainViewContainer title="Shift Settings">
      {!loading ? (
        <Stack>
          <Tooltip title="Allows staff to override the compliance check when using the Assign to Shift modal">
            <FormControlLabel
              label={'Enable Compliance Override'}
              control={
                <Checkbox
                  checked={complianceOverride}
                  onClick={() => {
                    setComplianceOverride(!complianceOverride);
                  }}
                />
              }
            />
          </Tooltip>
          {smsAutoApply !== null && (
            <>
              <FormControlLabel
                label={'SMS YES adds the worker to Applied'}
                control={
                  <Checkbox
                    checked={smsAutoApply}
                    onClick={() => {
                      setSmsAutoApply(!smsAutoApply);
                    }}
                  />
                }
              />
              <FormHelperText>
                {!smsAutoApply
                  ? 'YES replies are recorded, but nobody is added to Applied automatically.'
                  : complianceOverride
                  ? "Workers who reply YES are added to Applied, even with missing documents or trainings (they're flagged for review)."
                  : 'Workers who reply YES are added to Applied only if fully compliant. Others are flagged for review.'}
              </FormHelperText>
            </>
          )}
          <Box sx={{display: 'flex', justifyContent: 'center'}}>
            <Button
              onClick={handleSave}
              variant="outlined"
              disabled={saving}
              sx={{
                width: '250px',
                mt: 4,
                '&.Mui-disabled': {color: 'text.secondary'},
              }}
            >
              {saving ? <CircularProgress size={24} color="inherit" /> : 'Save'}
            </Button>
          </Box>
        </Stack>
      ) : (
        <Box sx={{display: 'flex', justifyContent: 'center'}}>
          <CircularProgress />
        </Box>
      )}
      <Toast
        open={showToast}
        handleClose={() => setShowToast(false)}
        text={toastData.text}
        severity={toastData.severity}
        verticalPos={toastData.verticalPos}
        horizontalPos={toastData.horizontalPos}
      />
    </MainViewContainer>
  );
};
